import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAlmaAvatar } from '../../hooks/useAlmaAvatar';
import { supabase } from '../../lib/supabase';
import posthog from '../../lib/posthog';
import { formatPlaces } from '../../lib/places';
import {
  BOTTOM_SHEET_TRANSITION_MS,
  BottomSheet,
  Button,
  ConfirmBar,
  HostChoiceCard,
  HostSwipeCard,
  Logo,
  PhotoGrid,
  PhotoLightbox,
  RevealStyles,
  Spinner,
  reveal,
  useRevealSettle,
} from '../../components/design-system';
import type { HostChoiceReason } from '../../components/design-system';

interface TripLeg {
  id: string;
  city: string;
  start_date: string;
  days: number;
}

interface ProposedMatch {
  match_id: string;
  /** The city of the trip this host is proposed for (null for a trip matched as one block). */
  leg_id: string | null;
  host_id: string;
  rank: number | null;
  match_note: string | null;
  match_reasons: string[];
  full_name: string | null;
  avatar_url: string | null;
  headline: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  specialties: string[] | null;
  response_time: string | null;
  moodboard_images: string[] | null;
}

interface MatchSelectRow {
  id: string;
  leg_id: string | null;
  host_id: string;
  rank: number | null;
  match_note: string | null;
  match_reasons: string[] | null;
  status: string;
  profiles: { full_name: string | null; avatar_url: string | null } | null;
}

interface HostProfileRow {
  profile_id: string;
  headline: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  specialties: string[] | null;
  response_time: string | null;
  moodboard_images: string[] | null;
}

const REASON_ICONS = ['map-pin', 'coffee', 'users', 'book', 'walk', 'music'];

function firstNameOf(name: string | null): string {
  return name?.trim().split(/\s+/)[0] ?? 'them';
}

export function MatchSelection() {
  const { user, loading } = useAuth();
  const almaAvatar = useAlmaAvatar();
  const navigate = useNavigate();
  const [allMatches, setMatches] = useState<ProposedMatch[]>([]);
  // A trip with several cities is matched city by city: one question per city,
  // each with its own two hosts, then one payment for the whole trip.
  const [legs, setLegs] = useState<TripLeg[]>([]);
  const [legIndex, setLegIndex] = useState(0);
  // The host chosen for each city already answered, in trip order.
  const [picks, setPicks] = useState<string[]>([]);
  const currentLeg = legs.length > 1 ? (legs[legIndex] ?? null) : null;
  const matches = currentLeg ? allMatches.filter((m) => m.leg_id === currentLeg.id) : allMatches;
  const [fetchLoading, setFetchLoading] = useState(true);
  const [chosen, setChosen] = useState<ProposedMatch | null>(null);
  const [confirming, setConfirming] = useState(false);
  // The mobile swipe card's "Know more" opens this — the full detail (reasons,
  // specialties, portfolio) that lives directly on the desktop card instead.
  const [knowMore, setKnowMore] = useState<ProposedMatch | null>(null);
  const [closingSheet, setClosingSheet] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Every way of dismissing the "Know more" sheet (backdrop tap, or the
  // Choose button below) routes through here so it always plays the
  // slide-down close instead of just vanishing — `knowMore` itself has to
  // stay set for the animation's duration since the sheet's content reads
  // straight from it.
  function closeSheet() {
    setClosingSheet(true);
    setTimeout(() => {
      setKnowMore(null);
      setClosingSheet(false);
    }, BOTTOM_SHEET_TRANSITION_MS);
  }
  const [portraitId, setPortraitId] = useState<string | null>(null);
  const [tripCities, setTripCities] = useState<string[]>([]);
  // reveal() claims each card's opacity/transform/filter with fill-mode
  // 'both' so it holds its end state — but that would also block the later
  // chosen/dimmed opacity change. Once the staggered reveal has definitely
  // finished, stop passing the animation style so chosen/dimmed can take
  // over the same properties normally.
  const cardsRevealed = useRevealSettle(matches.length, { baseDelay: 0.85, stagger: 0.16, duration: 0.8 });

  // The Alma-quote paragraph next to the avatar: CSS `width: fit-content` on
  // a flex item's wrapping text doesn't reliably shrink to the actual
  // widest rendered line on WebKit (confirmed — it worked in a Chromium
  // preview but not on-device), even though it's spec-correct in theory. So
  // instead of trusting a CSS keyword, measure the real rendered bounding
  // box of the wrapped text with the Range API (a much older, universally
  // consistent technique) and set that as an explicit pixel width — this is
  // the same outcome as "a fixed width set right at the point where it
  // hugs the content", just computed from the actual render instead of
  // eyeballed as a constant that could drift with font metrics per device.
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const [quoteWidth, setQuoteWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    function measure() {
      const el = quoteRef.current;
      if (!el) return;
      // Reset to the known-good baseline before measuring — otherwise this
      // measures against whatever (possibly already-too-narrow) width a
      // previous measurement pass left applied, which can only ever get
      // narrower on each re-measure, never correct itself.
      const previousWidth = el.style.width;
      el.style.width = '220px';
      const range = document.createRange();
      range.selectNodeContents(el);
      const measured = range.getBoundingClientRect().width;
      el.style.width = previousWidth;
      // No `text-wrap: pretty` on this element — its line-balancing rewraps
      // text into MORE, shorter lines than a plain greedy wrap would, so a
      // width measured from its widest line is too narrow for a normal
      // 2-line wrap once re-applied. Greedy (default) wrap is what makes
      // "shrink to the widest rendered line" self-consistent.
      if (measured > 0) setQuoteWidth(Math.ceil(measured));
    }
    measure();
    // The initial measure can fire before "Hedvig Letters Serif" has
    // actually loaded (it's a Google Fonts <link>, not blocking) — measuring
    // against the fallback font's narrower metrics locks in a width that's
    // too tight once the real font swaps in, forcing extra line wraps. Once
    // the font is confirmed ready, re-measure against the real glyphs.
    document.fonts?.ready.then(measure);
  }, [matches.length]);

  // Mobile carousel: the centered card grows to scale(1) while the peeking
  // side card sits at scale(0.9), interpolated continuously by scroll
  // position — not snapped after the fact — so it visibly grows into focus
  // as it centers. Driven by refs + a scroll listener, not React state: a
  // per-frame setState here would re-render on every scroll tick, which is
  // exactly the kind of main-thread work that caused a real hitch earlier
  // in this carousel's history. transform:scale() doesn't affect layout, so
  // this can't disturb the gap/spacing between cards or anything below them.
  //
  // The scale is applied to an INNER div, not the `scroll-snap-align`
  // element itself (`cardRefs`) — putting a live-updating transform
  // directly on the snap target is a known WebKit quirk that can throw off
  // where Safari actually settles the snap, which is what caused the
  // focused card to rest slightly off-center on device. Keeping the
  // snap-target untransformed and scaling a child instead sidesteps that
  // entirely. Distance is measured via getBoundingClientRect (viewport
  // coordinates) rather than offsetLeft, which depends on the nearest
  // positioned ancestor and is easy to get subtly wrong.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scaleRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let raf = 0;
    function updateScales() {
      raf = 0;
      const el = scrollerRef.current;
      if (!el) return;
      const containerRect = el.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      cardRefs.current.forEach((card, i) => {
        const scaleEl = scaleRefs.current[i];
        if (!card || !scaleEl) return;
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - containerCenter);
        const normalized = Math.min(1, distance / (containerRect.width / 2));
        const scale = 1 - 0.1 * normalized;
        scaleEl.style.transform = `scale(${scale})`;
      });
    }

    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(updateScales);
    }

    updateScales();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateScales);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateScales);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [matches.length]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load(travelerId: string) {
      // Anchor on the current journey: the latest portrait.
      const { data: portrait } = await supabase
        .from('taste_portraits')
        .select('id, destinations')
        .eq('traveler_id', travelerId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;
      if (!portrait) {
        navigate('/try', { replace: true });
        return;
      }
      setPortraitId(portrait.id);
      setTripCities((portrait as { destinations: string[] | null }).destinations ?? []);

      // Already committed to a host for this journey — paid (pending host
      // confirmation) or host-confirmed (accepted)? It's a live trip now.
      const { data: committed } = await supabase
        .from('matches')
        .select('id')
        .eq('portrait_id', portrait.id)
        .in('status', ['pending', 'accepted'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;
      if (committed) {
        navigate(`/trip/${committed.id}/chat`, { replace: true });
        return;
      }

      const { data: matchRows } = await supabase
        .from('matches')
        .select(
          'id, host_id, leg_id, rank, match_note, match_reasons, status, profiles!matches_host_id_fkey(full_name, avatar_url)'
        )
        .eq('traveler_id', travelerId)
        .eq('portrait_id', portrait.id)
        .eq('status', 'proposed')
        .order('rank', { ascending: true });

      if (cancelled) return;

      const rows = (matchRows as unknown as MatchSelectRow[]) ?? [];
      if (rows.length === 0) {
        setMatches([]);
        setFetchLoading(false);
        return;
      }

      const hostIds = rows.map((r) => r.host_id);
      const [{ data: hostRows }, { data: destLinks }, { data: legRows }] = await Promise.all([
        supabase
          .from('host_profiles')
          .select('profile_id, headline, bio, city, country, specialties, response_time, moodboard_images')
          .in('profile_id', hostIds),
        supabase
          .from('host_destinations')
          .select('host_id, destinations(name)')
          .in('host_id', hostIds),
        supabase
          .from('trip_legs')
          .select('id, city, start_date, days')
          .eq('portrait_id', portrait.id)
          .order('position'),
      ]);

      if (cancelled) return;

      const hostMap = new Map<string, HostProfileRow>();
      ((hostRows as HostProfileRow[]) ?? []).forEach((h) => {
        hostMap.set(h.profile_id, h);
      });

      // Full set of cities per host (a host can operate in more than one).
      const cityMap = new Map<string, string[]>();
      ((destLinks as { host_id: string; destinations: { name: string } | null }[] | null) ?? []).forEach((l) => {
        if (!l.destinations?.name) return;
        const arr = cityMap.get(l.host_id) ?? [];
        arr.push(l.destinations.name);
        cityMap.set(l.host_id, arr);
      });

      const merged: ProposedMatch[] = rows.map((r) => {
        const h = hostMap.get(r.host_id);
        const cities = cityMap.get(r.host_id) ?? [];
        return {
          match_id: r.id,
          leg_id: r.leg_id ?? null,
          host_id: r.host_id,
          rank: r.rank,
          match_note: r.match_note,
          match_reasons: r.match_reasons ?? [],
          full_name: r.profiles?.full_name ?? null,
          avatar_url: r.profiles?.avatar_url ?? null,
          headline: h?.headline ?? null,
          bio: h?.bio ?? null,
          // Prefer the full destination list; fall back to the legacy single city.
          city: cities.length ? cities.join(' · ') : (h?.city ?? null),
          country: h?.country ?? null,
          specialties: h?.specialties ?? null,
          response_time: h?.response_time ?? null,
          moodboard_images: h?.moodboard_images ?? null,
        };
      });

      // Only the cities that have a shortlist become questions.
      const withHosts = new Set(rows.map((r) => r.leg_id).filter((id): id is string => !!id));
      setLegs(((legRows as TripLeg[] | null) ?? []).filter((l) => withHosts.has(l.id)));
      setMatches(merged);
      setFetchLoading(false);
    }

    void load(user.id);
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  // Picking a host no longer commits anything — the traveler pays first, and
  // the payment is what moves the match to `pending`. Carry the chosen match
  // (and its journey) into the payment step.
  function handleConfirm() {
    if (!chosen || !portraitId) return;
    posthog.capture('host_match_selected', {
      match_rank: chosen.rank,
      ...(currentLeg ? { leg: legIndex + 1, legs: legs.length } : {}),
    });
    // More cities to choose for: remember this one and ask about the next city.
    if (currentLeg && legIndex < legs.length - 1) {
      setPicks((p) => [...p.slice(0, legIndex), chosen.match_id]);
      setChosen(null);
      setConfirming(false);
      setKnowMore(null);
      setLegIndex(legIndex + 1);
      window.scrollTo?.({ top: 0 });
      return;
    }
    const matchIds = currentLeg ? [...picks.slice(0, legIndex), chosen.match_id] : [chosen.match_id];
    navigate('/payment', { state: { matchId: matchIds[0], matchIds, portraitId } });
  }

  // Back to the previous city's question; its choice is asked again.
  function handleBackToPreviousCity() {
    setChosen(null);
    setConfirming(false);
    setKnowMore(null);
    setPicks((p) => p.slice(0, legIndex - 1));
    setLegIndex(legIndex - 1);
    window.scrollTo?.({ top: 0 });
  }

  if (loading || fetchLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: 'var(--bg-deep)' }}>
        <Spinner />
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center px-6 text-center gap-4" style={{ background: 'var(--bg-deep)' }}>
        <p className="text-sm text-app-muted leading-relaxed max-w-xs">
          Alma is still finding the right hosts for you.
        </p>
        <Link
          to="/try"
          className="text-sm font-medium underline underline-offset-2 transition-all"
          style={{ color: 'var(--accent)' }}
        >
          Back to chat
        </Link>
      </div>
    );
  }

  // The trip's own cities first — a two-city trip must not be labelled with one.
  // With several cities, each question is about one city.
  const city = currentLeg ? currentLeg.city : (formatPlaces(tripCities) || matches[0]?.city || 'your city');
  const stepOf = currentLeg ? `Host ${legIndex + 1} of ${legs.length} · ` : '';
  const nextCity = currentLeg && legIndex < legs.length - 1 ? legs[legIndex + 1].city : null;
  const chosenFirst = chosen ? firstNameOf(chosen.full_name) : '';
  const countWord = matches.length === 2 ? 'two people' : matches.length === 1 ? 'someone' : `${matches.length} people`;

  return (
    <div className="min-h-dvh flex flex-col text-app-fg font-sans" style={{ background: 'var(--bg-deep)' }}>
      <RevealStyles />

      {/* Header — desktop only. Dropped on mobile entirely so the intro and
          cards below get the vertical space it was taking. */}
      <header
        className="hidden md:flex shrink-0 items-center justify-center relative pt-safe-or-5 pb-5 px-5 md:px-8"
        style={{ borderBottom: '1px solid var(--line)', ...reveal('fade', 0) }}
      >
        <Logo variant="full" height={20} />
      </header>

      {/* Mobile intro — compact: headline, then Alma's avatar + a left-aligned
          quote sitting beside it (one row instead of two centered blocks),
          then the city pill. No header above it, so this carries the top
          safe-area padding itself. */}
      <div className="md:hidden shrink-0 w-full flex flex-col items-center text-center px-6 pt-safe-or-10">
        <h1
          className="font-serif text-app-fg"
          style={{
            fontSize: 'var(--fs-page-title)', fontWeight: 400, lineHeight: 'var(--lh-page-title)', letterSpacing: 'var(--ls-page-title)', textWrap: 'pretty', marginBottom: 24,
            ...reveal('rise', 0.12, 0.7),
          }}
        >
          Choose your host
        </h1>
        <div
          className="flex items-start gap-[10px] text-left"
          style={{ width: 'fit-content', marginBottom: 22, ...reveal('fade', 0.32) }}
        >
          {almaAvatar ? (
            <img src={almaAvatar} alt="Alma" className="w-[34px] h-[34px] rounded-full object-cover shrink-0" style={{ border: '0.5px solid rgb(var(--cream) / .18)' }} />
          ) : (
            <div className="w-[34px] h-[34px] rounded-full avatar-alma flex items-center justify-center font-serif text-[13px] shrink-0">A</div>
          )}
          <p
            ref={quoteRef}
            className="font-serif text-app-fg-2"
            style={{ width: quoteWidth ?? 220, maxWidth: 220, fontSize: 14, lineHeight: 1.42, marginTop: 3 }}
          >
            "I found {countWord} who understand how you travel."
          </p>
        </div>
        <div
          className="inline-flex items-center gap-[7px]"
          style={{
            background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: '7px 16px',
            marginBottom: 24,
            ...reveal('scale', 0.58),
          }}
        >
          <i className="ti ti-map-pin" style={{ fontSize: 12, color: 'rgb(var(--gold))' }} />
          <span className="text-xs text-app-muted">{stepOf}Hosts for</span>
          <span className="font-serif text-[13px] text-app-fg">{city}</span>
        </div>
      </div>

      {/* Desktop intro — unchanged, centered layout with room to spare. */}
      <div className="hidden md:flex shrink-0 w-full flex-col items-center text-center px-8" style={{ paddingTop: 'clamp(24px, 6vw, 40px)' }}>
        <h1
          className="font-serif text-app-fg"
          style={{
            fontSize: 'var(--fs-page-title)', fontWeight: 400, lineHeight: 'var(--lh-page-title)', letterSpacing: 'var(--ls-page-title)', textWrap: 'pretty', marginBottom: 18,
            ...reveal('rise', 0.12, 0.7),
          }}
        >
          Choose your host
        </h1>
        <div
          className="flex items-center justify-center gap-[9px]"
          style={{ marginBottom: 10, ...reveal('fade', 0.32) }}
        >
          {almaAvatar ? (
            <img src={almaAvatar} alt="Alma" className="w-[26px] h-[26px] rounded-full object-cover" style={{ border: '0.5px solid rgb(var(--cream) / .18)' }} />
          ) : (
            <div className="w-[26px] h-[26px] rounded-full avatar-alma flex items-center justify-center font-serif text-[11px]">A</div>
          )}
          <span className="text-[11px] text-app-muted">Alma · AI assistant</span>
        </div>
        <p
          className="font-serif text-app-fg-2"
          style={{
            fontSize: 16, lineHeight: 1.5, textWrap: 'pretty', maxWidth: '40ch', marginBottom: 16,
            ...reveal('rise', 0.42, 0.6),
          }}
        >
          "I found {countWord} who understand how you travel."
        </p>
        <div
          className="inline-flex items-center gap-[7px]"
          style={{
            background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: '7px 16px',
            ...reveal('scale', 0.58),
          }}
        >
          <i className="ti ti-map-pin" style={{ fontSize: 12, color: 'rgb(var(--gold))' }} />
          <span className="text-xs text-app-muted">{stepOf}Hosts for</span>
          <span className="font-serif text-[13px] text-app-fg">{city}</span>
        </div>
      </div>

      {/* Host cards — the actual reveal payoff: each comes into focus out of
          a soft blur, staggered, rather than just fading in like the copy above.
          Mobile: full-bleed swipeable cards, one screen's worth of scroll away
          from choosing instead of a long list. Desktop keeps the side-by-side
          cards, which already show both hosts without scrolling. */}
      <div
        ref={scrollerRef}
        className="md:hidden flex-1 flex overflow-x-auto snap-x snap-mandatory pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ paddingBottom: confirming ? 148 : undefined, scrollPaddingLeft: 56, scrollPaddingRight: 56 }}
      >
        {/* Real spacer elements, not padding — a boundary card (first/last)
            can only be scroll-snap-centered if there's actual scrollable
            room on its outer side to shift into. CSS padding isn't part of
            the scrollable content, so it can't provide that room; these
            flex items can. The spacer width also doubles as the "peek"
            amount: card width is 100% minus twice the spacer, so the
            centered card always leaves that much of its neighbor visible
            on each side as a scroll hint. Centering math only depends on
            spacer === (100% - card width) / 2, so this stays exact
            regardless of the peek amount chosen. */}
        <div style={{ width: 56, flexShrink: 0 }} aria-hidden="true" />
        {matches.map((match, i) => (
          <div
            key={match.match_id}
            ref={(el) => { cardRefs.current[i] = el; }}
            className="snap-center"
            style={{ width: 'calc(100% - 112px)', flexShrink: 0, marginRight: i < matches.length - 1 ? 16 : 0 }}
          >
            <div ref={(el) => { scaleRefs.current[i] = el; }} style={{ width: '100%', height: '100%', willChange: 'transform' }}>
              <HostSwipeCard
                name={match.full_name ?? 'Your host'}
                avatarUrl={match.avatar_url}
                headline={match.headline}
                almaNote={match.match_note ?? undefined}
                state={
                  chosen
                    ? (chosen.match_id === match.match_id ? 'chosen' : 'dimmed')
                    : 'default'
                }
                onKnowMore={() => setKnowMore(match)}
                style={cardsRevealed ? undefined : reveal('focus', 0.85 + i * 0.16)}
              />
            </div>
          </div>
        ))}
        <div style={{ width: 56, flexShrink: 0 }} aria-hidden="true" />
      </div>

      {/* Set expectations right under the cards, as short as possible — a
          request, not a booking — rather than up front delaying the cards. */}
      <p
        className="md:hidden text-app-muted text-center shrink-0 pb-safe-or-6 mx-auto"
        style={{ fontSize: 10.5, marginTop: 12, maxWidth: '40ch', lineHeight: 1.5, paddingLeft: 24, paddingRight: 24, ...reveal('fade', 0.7) }}
      >
        This sends a request, not a booking — the host still has to confirm.
      </p>

      <div className="hidden md:flex flex-1 md:flex-row gap-6 p-5 md:p-8 max-w-[860px] w-full mx-auto items-stretch" style={{ paddingBottom: confirming ? 140 : undefined }}>
        {matches.map((match, i) => (
          <HostChoiceCard
            key={match.match_id}
            name={match.full_name ?? 'Your host'}
            avatarUrl={match.avatar_url}
            headline={match.headline}
            city={match.city ?? undefined}
            responseTime={match.response_time ? `Replies in ${match.response_time}` : undefined}
            photos={match.moodboard_images ?? []}
            almaNote={match.match_note ?? undefined}
            reasons={match.match_reasons.map((text, i): HostChoiceReason => ({ icon: REASON_ICONS[i % REASON_ICONS.length], text }))}
            specialties={match.specialties ?? []}
            state={
              chosen
                ? (chosen.match_id === match.match_id ? 'chosen' : 'dimmed')
                : 'default'
            }
            onChoose={() => {
              if (!chosen) { setChosen(match); setConfirming(true); }
            }}
            style={cardsRevealed ? undefined : reveal('focus', 0.85 + i * 0.16)}
          />
        ))}
      </div>

      <p
        className="hidden md:block text-xs text-app-muted text-center shrink-0"
        style={{ margin: '0 auto 24px', maxWidth: '48ch', lineHeight: 1.6, padding: '0 20px', ...reveal('fade', 0.7) }}
      >
        Pick the host you'd prefer and confirm to send your request. They'll review it and
        confirm before your trip is booked — choosing here doesn't guarantee them yet.
      </p>

      {/* "Know more" — the mobile swipe card's detail, moved off the card
          entirely into a sheet: reasons, specialties, portfolio. */}
      {knowMore && (
        <BottomSheet ariaLabel={knowMore.full_name ?? 'Your host'} onClose={closeSheet} closing={closingSheet}>
          <div style={{ padding: '4px 12px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2
              className="font-serif text-app-fg"
              style={{
                fontSize: 'var(--fs-page-title)', lineHeight: 'var(--lh-page-title)', letterSpacing: 'var(--ls-page-title)',
                marginBottom: 12,
              }}
            >
              {knowMore.full_name ?? 'Your host'}
            </h2>
            {(knowMore.city || knowMore.response_time) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
                {knowMore.city ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <i className="ti ti-map-pin" style={{ fontSize: 12 }} />{knowMore.city}
                  </span>
                ) : null}
                {knowMore.response_time ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <i className="ti ti-clock" style={{ fontSize: 12 }} />Replies in {knowMore.response_time}
                  </span>
                ) : null}
              </div>
            )}

            {knowMore.match_reasons.length ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 10, marginBottom: 14 }}>
                  <span style={{ color: 'rgb(var(--gold))', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>Why you'll connect</span>
                  <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginBottom: 20 }}>
                  {knowMore.match_reasons.map((text, i) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <i className={`ti ti-${REASON_ICONS[i % REASON_ICONS.length]}`} style={{ color: 'var(--text-dim)', fontSize: 12, marginTop: 2, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.58, textWrap: 'pretty' }}>{text}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {knowMore.specialties?.length ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
                  <span style={{ color: 'rgb(var(--gold))', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>Specialties</span>
                  <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                  {knowMore.specialties.map((s) => (
                    <span key={s} style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--text-soft)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: '4px 10px' }}>
                      {s}
                    </span>
                  ))}
                </div>
              </>
            ) : null}

            {knowMore.moodboard_images?.length ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
                  <span style={{ color: 'rgb(var(--gold))', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>Photos</span>
                  <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
                </div>
                <PhotoGrid
                  images={knowMore.moodboard_images}
                  count={9}
                  columns={3}
                  gap={3}
                  radius={8}
                  alt={`${firstNameOf(knowMore.full_name)}'s photo`}
                  style={{ marginBottom: 20 }}
                  onCellClick={(i) => { if (knowMore.moodboard_images?.[i]) setLightboxIndex(i); }}
                />
              </>
            ) : null}

            {/* The actual selection happens here, not on the card — see
                HostSwipeCard. Sheet closes into the existing ConfirmBar. */}
            <Button
              fullWidth
              size="lg"
              disabled={chosen?.match_id === knowMore.match_id}
              onClick={() => {
                if (!chosen) { setChosen(knowMore); setConfirming(true); }
                closeSheet();
              }}
            >
              {chosen?.match_id === knowMore.match_id ? 'Chosen' : `Choose ${firstNameOf(knowMore.full_name)}`}
            </Button>
          </div>
        </BottomSheet>
      )}

      {knowMore && lightboxIndex !== null && knowMore.moodboard_images && (
        <PhotoLightbox
          images={knowMore.moodboard_images.filter((src): src is string => !!src)}
          startIndex={lightboxIndex}
          alt={`${firstNameOf(knowMore.full_name)}'s photo`}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {currentLeg && legIndex > 0 && !confirming && (
        <div className="shrink-0 flex justify-center pb-6">
          <button
            type="button"
            onClick={handleBackToPreviousCity}
            className="text-sm underline underline-offset-2"
            style={{ color: 'var(--accent)' }}
          >
            Back to {legs[legIndex - 1].city}
          </button>
        </div>
      )}

      {/* Confirm banner — proceeds to payment, not a final commit */}
      {confirming && chosen && (
        <ConfirmBar
          title={currentLeg ? `Going with ${chosenFirst} in ${currentLeg.city}?` : `Going with ${chosenFirst}?`}
          subtitle={nextCity
            ? `Next, choose your host in ${nextCity}. You pay once, after the last choice.`
            : `Confirm and pay to send your request — ${chosenFirst} still needs to accept before it's booked.`}
          confirmLabel={nextCity ? `Next: ${nextCity}` : 'Confirm & pay'}
          cancelLabel="Change my mind"
          onConfirm={handleConfirm}
          onCancel={() => { setChosen(null); setConfirming(false); }}
        />
      )}
    </div>
  );
}

export default MatchSelection;
